import { useEffect, useState } from "react";
import { Cow } from "../../../../models/cow";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../services/firebase.config";
import { DataGrid, GridActionsCellItem, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { frFR } from '@mui/x-data-grid/locales';
import { cowColumns } from "./helpers/CowColumns";
import { FaTimes, FaTrash, FaUpload } from "react-icons/fa";
import { Button, Grid, Alert, AlertTitle, CircularProgress, Box } from "@mui/material";
import { addDoc } from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import Error from "../../../../components/Error/Error";
import { CustomInput, CustomLabel } from "./styles";
import DialogDelete from "../helpers/DialogDelete";

const fileReader = new FileReader();

const CowPart = () => {
    const [cows, setCows] = useState<Cow[]>([]);
    const [selectedCow, setSelectedCow] = useState<Cow | null>(null);
    const [deleteAllCows, setDeleteAllCows] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fetchCows = async () => {
        try {
            const data = await getDocs(collection(db, "vaches"));
            if (data.empty) {
                setCows([]);
            } else {
                setCows(data.docs.map(doc => doc.data() as Cow));
            }
        } catch (error: any) {
            setError(error.message);
        }

    };

    useEffect(() => {
        fetchCows();
    }, []);

    const getCowsFromFile = (fileAsString: String) => {
        setCows([]);
        const csvRows = fileAsString.split(/\r\n?\n/);
        const dataOfCsv: Cow[] = [];
        csvRows.forEach((row: string) => {
            if (row !== "") {
                const rowValuesTranform = row.split('"');
                const rowValues = rowValuesTranform[0].split(',');
                if (rowValuesTranform.length > 1) {
                    const rowValuesSecondPart = rowValuesTranform[2].split(',');
                    rowValuesSecondPart.shift();
                    rowValuesSecondPart.forEach((value: string) => {
                        rowValues.push(value);
                    });
                    rowValues[12] = rowValuesTranform[1]
                }
                dataOfCsv.push({
                    Num_catalogue: rowValues[0],
                    Num_section: rowValues[1],
                    Categorie: rowValues[2],
                    Nom_court: rowValues[3],
                    Position_affixe: rowValues[4],
                    Affixe: rowValues[5],
                    Nom_long: rowValues[6],
                    Robe: rowValues[7],
                    Pere: rowValues[8],
                    GPM: rowValues[9],
                    AGPM: rowValues[10],
                    Rang_lact: rowValues[11],
                    Proprietaires: rowValues[12],
                    dept_proprietaire: rowValues[13],
                    Naisseur: rowValues[14],
                    Dept_naisseur: rowValues[15],
                    Num_lact: rowValues[16],
                    Lait: rowValues[17],
                    TA: rowValues[18],
                    TB: rowValues[19],
                    MU: rowValues[20],
                    Note_globale: rowValues[21],
                    Version_table: rowValues[22],
                    Age_au_pointage: rowValues[23],
                    Date_naissance: rowValues[24],
                });
            }
        });
        if (dataOfCsv.length > 0) {
            dataOfCsv.shift();
            let erreurInsertion = 0;
            let numberItems = 0;
            dataOfCsv.forEach(async (cow: Cow) => {
                setIsLoading(true);
                if (cow.Num_catalogue !== "") {
                    await addDoc(collection(db, "vaches"), cow);
                }
                else {
                    erreurInsertion++;
                }
                numberItems++;
                if (numberItems === dataOfCsv.length) {
                    if (erreurInsertion === 0) {
                        toast.success("Les vaches ont bien été ajoutées", { position: "bottom-center", theme: "colored" });
                    } else {
                        toast.error(`Une erreur est survenue lors de l'insertion de ${erreurInsertion} vache(s)`, { position: "bottom-center", theme: "colored" });
                    }
                    setIsLoading(false);
                    fetchCows();
                }
            });
        } else {
            toast.error("Le fichier CSV sélectionner ne contient aucune Vache", { position: "bottom-center", theme: "colored" });
        }

    };

    const onSetCsvFile = (e: any) => {
        if (e?.target?.files[0]) {
            fileReader.onload = ({ target }) => getCowsFromFile(target?.result as String);
            fileReader.readAsText(e.target.files[0], 'ISO-8859-1');
        }
        // réinitialisation de l'event pour pouvoir recharger le même fichier
        e.target.value = '';
    };

    const getActions = (params: { row: Cow }) => [
        <GridActionsCellItem
            key="answer"
            icon={<FaTrash />}
            label="Suppresssion d'une vache"
            onClick={() => { setSelectedCow(params.row); setIsOpen(true); setDeleteAllCows(false); }}
        />,
    ];

    const columns: GridColDef[] = [
        ...cowColumns,
        {
            field: 'Supprimer',
            type: 'actions',
            flex: 1,
            getActions,
        } as GridColDef,
    ];

    return (
        <>
            <Grid container mb={1} spacing={1} justifyContent={"flex-end"}>
                <Grid size={2}>
                    <CustomLabel htmlFor="upload-csv">
                        <CustomInput
                            type="file"
                            id="upload-csv"
                            accept=".csv"
                            onChange={onSetCsvFile}
                        />
                        <Button style={{ height: 55 }} color="primary" fullWidth variant="contained" component="span" aria-label="Sélectionner un fichier" startIcon={<FaUpload />}>
                            Ajouter un CSV
                        </Button>
                    </CustomLabel>
                </Grid>
                <Grid>
                    <Button
                        style={{ height: 55 }}
                        variant="contained"
                        color="error"
                        size="large"
                        disabled={cows.length === 0}
                        onClick={() => { setDeleteAllCows(true); setIsOpen(true); }}
                    >
                        <FaTimes />
                    </Button>
                </Grid>
            </Grid>
            {!!cows.length ? (
                <DataGrid
                    getRowId={(row) => row.Num_catalogue}
                    columns={columns}
                    rows={cows}
                    localeText={frFR.components.MuiDataGrid.defaultProps.localeText}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            csvOptions: {
                                fileName: "Vaches",
                                utf8WithBom: true,
                            }
                        }
                    }}
                />
            ) : !error ? (
                !isLoading ? (
                    <Alert severity="info">
                        <AlertTitle>Base de Données</AlertTitle>
                        Il n'y a aucune vache en base de données
                    </Alert>
                ) :
                    (
                        <Box justifyContent="center" alignItems="center" display="flex" width="100%" minHeight="25rem">
                            <CircularProgress />
                        </Box>
                    )
            ) : (
                <Error message={error} />
            )}

            <ToastContainer />
            <DialogDelete
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                typeObject="vaches"
                item={selectedCow}
                allItems={deleteAllCows}
                reloadDatas={fetchCows}
            />
        </>
    );
};

export default CowPart;